import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ValidationErrors,
  ValidatorFn,
  AbstractControl,
} from "@angular/forms";
import { Router } from "@angular/router";
import { PublicDisputeResolutionService } from "src/app/shared/services/public-dispute-resolution.service";
import { CreateUserRequest } from "src/app/shared/models/user";
import { NotificationService } from "src/app/shared/services/notification.service";
import { NgRecaptcha3Service } from "ng-recaptcha3";
import { environment } from "src/environments/environment";
import { passwordPolicyValidator } from "src/app/shared/validators";

@Component({
  selector: "app-thank-you-create-account",
  templateUrl: "./thank-you-create-account.component.html",
  styleUrls: ["./thank-you-create-account.component.scss"],
  standalone: false,
})
export class ThankYouCreateAccountComponent implements OnInit, OnDestroy {
  email: string;
  firstName: string;
  lastName: string;
  middleName: string;
  phone: string;
  street1: string;
  street2: string;
  city: string;
  stateValue: string;
  zip: string;
  form: FormGroup;
  userExists: boolean = false;
  loading: boolean = true;
  hidePassword = true;
  hidePassword2 = true;

  get password() {
    return this.form.get("password");
  }

  get confirmedPassword() {
    return this.form.get("confirmedPassword");
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private publicDisputeResolutionService: PublicDisputeResolutionService,
    private notificationService: NotificationService,
    private recaptcha3: NgRecaptcha3Service
  ) {
    const nav = this.router.currentNavigation();
    this.email = nav?.extras.state?.email || "";
    this.firstName = nav?.extras.state?.firstName || "";
    this.lastName = nav?.extras.state?.lastName || "";
    this.middleName = nav?.extras.state?.middleName || "";
    this.phone = nav?.extras.state?.phone || "";
    this.street1 = nav?.extras.state?.street1 || "";
    this.street2 = nav?.extras.state?.street2 || "";
    this.city = nav?.extras.state?.city || "";
    this.stateValue = nav?.extras.state?.state || "";
    this.zip = nav?.extras.state?.zip || "";
    if (!this.email) {
      this.router.navigate(["/"]);
      return;
    }
    this.form = this.fb.group(
      {
        password: [
          "",
          [
            Validators.required,
            Validators.maxLength(24),
            Validators.minLength(12),
            passwordPolicyValidator(),
          ],
        ],
        confirmPassword: ["", [Validators.required]],
        firstName: [this.firstName],
        lastName: [this.lastName],
        middleName: [this.middleName],
        phone: [this.phone],
        street1: [this.street1],
        street2: [this.street2],
        city: [this.city],
        state: [this.stateValue],
        zip: [this.zip],
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  ngOnInit() {
    this.recaptcha3.init(environment.recaptcha.siteKey).then((status) => {});
    this.publicDisputeResolutionService.checkUserExists(this.email).subscribe({
      next: (exists) => {
        this.userExists = exists;
        this.loading = false;
      },
      error: () => {
        this.userExists = false;
        this.loading = false;
      },
    });
  }

  passwordsMatchValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const password = group.get("password")?.value;
    const confirmPassword = group.get("confirmPassword")?.value;
    if (password && confirmPassword && password !== confirmPassword) {
      group.get("confirmPassword")?.setErrors({ notMatching: true });
      return { notMatching: true };
    }
    if (group.get("confirmPassword")?.hasError("notMatching")) {
      const errors = { ...group.get("confirmPassword")?.errors };
      delete errors["notMatching"];
      if (Object.keys(errors).length === 0) {
        group.get("confirmPassword")?.setErrors(null);
      } else {
        group.get("confirmPassword")?.setErrors(errors);
      }
    }
    return null;
  };

  onCreateAccount() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.recaptcha3.getToken().then((token) => {
      const req: CreateUserRequest = {
        email: this.email,
        password: this.form.value.password,
        firstName: this.form.value.firstName,
        lastName: this.form.value.lastName,
        phoneNumber: this.form.value.phone,
        addressLine1: this.form.value.street1,
        addressLine2: this.form.value.street2,
        city: this.form.value.city,
        state: this.form.value.state,
        zip: this.form.value.zip,
        token: token,
      };
      this.publicDisputeResolutionService.createPublicUser(req).subscribe({
        next: () => {
          this.router.navigate(["/dr-request/thank-you-verify-email"], {
            state: { fromCreateAccount: true },
          });
        },
        error: (err) => {
          const msg =
            err?.error?.message || "There was an error creating your account.";
          this.notificationService.alert(msg, "Error");
        },
      });
    });
  }

  getErrorMessage() {
    return this.password.hasError("required")
      ? "You must enter a value"
      : this.password.hasError("minlength")
      ? "Password must be at least 12 characters"
      : this.password.hasError("maxlength")
      ? "Password must be no greater than 24 characters"
      : this.password.hasError("ascend-password")
      ? "Password must contain at least one uppercase letter, at least one lowercase letter, and at least one number"
      : "";
  }

  public ngOnDestroy() {
    this.recaptcha3.destroy();
  }
}
